/**
 * @fileoverview Authentication configuration for Okta integration
 * 
 * This file contains the configuration needed for Okta authentication.
 * It retrieves necessary values from environment variables and constructs
 * the configuration object used by the OktaAuth instance in AppRouter.tsx.
 * 
 * The configuration follows Okta's recommended security practices including
 * the use of PKCE (Proof Key for Code Exchange) for secure authorization code flow.
 */

/**
 * Client ID from Okta application settings
 * Retrieved from environment variables to keep sensitive information out of source control
 */
const CLIENT_ID = process.env.REACT_APP_OKTA_CLIENT_ID || '';

/**
 * Okta domain/issuer for the authentication server
 * Retrieved from environment variables (e.g., "dev-12345.okta.com")
 */
const ISSUER = process.env.REACT_APP_OKTA_DOMAIN || '';

/**
 * Path where Okta will redirect after authentication
 * This must match the callback path configured in the Okta application settings
 */
const CALLBACK_PATH = '/login/callback';

/**
 * Current protocol (http: or https:) from the browser window
 * Used to construct the full redirect URI dynamically
 */
const PROTOCOL = window.location.protocol;

/**
 * Current host (domain and port if applicable) from the browser window
 * Used to construct the full redirect URI dynamically
 */
const HOST = window.location.host;

/**
 * Full redirect URI constructed from the current origin and callback path
 * This is the complete URL where Okta will redirect after authentication
 */
const REDIRECT_URI = `${PROTOCOL}//${HOST}${CALLBACK_PATH}`;

/**
 * Main configuration object for Okta authentication
 * 
 * This object is imported in AppRouter.tsx and used to initialize the OktaAuth instance.
 * It contains all necessary parameters for establishing a secure authentication flow.
 * 
 * @property {Object} oidc - OpenID Connect configuration settings
 * @property {string} oidc.clientId - Application's client ID from Okta
 * @property {string} oidc.issuer - URL of the OpenID Connect issuer
 * @property {string} oidc.redirectUri - URL where Okta will redirect after authentication
 * @property {string[]} oidc.scopes - OAuth scopes requested during authentication
 * @property {boolean} oidc.pkce - Whether to use PKCE flow for enhanced security
 */
const config = {
  oidc: {
    clientId: CLIENT_ID,                           // Application identifier in Okta
    issuer: `https://${ISSUER}/oauth2/default`,    // Authorization server URL
    redirectUri: REDIRECT_URI,                     // Where to redirect after authentication
    scopes: ['openid', 'profile', 'email', 'groups'], // Data access permissions requested
    pkce: true,                                    // Enable PKCE for enhanced security
  },
};

export default config;
